import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Ruler, HeartHandshake, KeyRound } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import PropertyCard from "@/components/PropertyCard";
import { useWhatsApp, WhatsAppIcon } from "@/components/Layout";
import { useSite } from "@/context/SiteContext";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBhcmNoaXRlY3R1cmFsJTIwaG91c2UlMjBleHRlcmlvciUyMG1pbmltYWxpc218ZW58MHx8fHwxNzkyNjAyNDEwfDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMAGE = "https://images.pexels.com/photos/15391694/pexels-photo-15391694.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PILARES = [
  { icon: Ruler, title: "Projetos bem resolvidos", text: "Plantas funcionais e espaços pensados para o dia a dia de quem vai morar." },
  { icon: ShieldCheck, title: "Qualidade construtiva", text: "Compromisso com materiais, acabamentos e processos em cada etapa da obra." },
  { icon: HeartHandshake, title: "Atendimento próximo", text: "Conversa direta com a equipe da Casa Vale, sem intermediários." },
  { icon: KeyRound, title: "Prontos para morar", text: "Imóveis concluídos: você visita, escolhe e recebe as chaves." },
];

export default function Home() {
  const { properties, loadingProps } = useSite();
  const openWa = useWhatsApp();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const featured = properties.filter((p) => p.featured).slice(0, 3);
  const shown = featured.length > 0 ? featured : properties.slice(0, 3);

  return (
    <div data-testid="home-page">
      <Seo
        title="Casa Vale | Casas e Apartamentos Prontos para Morar"
        description="Conheça os imóveis da Casa Vale Engenharia e Construção. Casas e apartamentos prontos, com conforto, qualidade construtiva e bem-estar para a sua família."
      />

      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMAGE} alt="Fachada de residência de alto padrão construída pela Casa Vale" className="w-full h-full object-cover scale-110" />
        </motion.div>
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-[#EAD9A8] text-sm sm:text-base font-extrabold uppercase tracking-[0.3em] mb-6"
            style={{ textShadow: "0 1px 10px rgba(9, 20, 14, 0.55)" }}
            data-testid="hero-eyebrow"
          >
            Casa Vale Engenharia e Construção
          </motion.p>
          <h1 className="font-serif-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-3xl" data-testid="hero-title">
            {["Seu novo lar", "começa aqui."].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            Conheça os imóveis da Casa Vale. Espaços planejados para proporcionar conforto, qualidade e bem-estar.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/imoveis"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200"
              data-testid="hero-cta-imoveis"
            >
              Conhecer Imóveis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              type="button"
              onClick={() => openWa("Olá! Vim pelo site da Casa Vale e gostaria de conhecer os imóveis disponíveis.")}
              className="inline-flex items-center gap-2 border border-white/50 hover:border-white text-white font-semibold px-7 py-3.5 rounded-full backdrop-blur-sm transition-colors duration-200"
              data-testid="hero-cta-whatsapp"
            >
              <WhatsAppIcon className="w-4 h-4" />
              Falar no WhatsApp
            </button>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section className="py-20 md:py-28 bg-white" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-brand text-xs font-bold uppercase tracking-[0.3em] mb-3">Portfólio</p>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight" data-testid="featured-heading">
                Imóveis em Destaque
              </h2>
              <Link to="/imoveis" className="text-sm font-bold text-brand hover:text-brand-dark inline-flex items-center gap-1.5" data-testid="featured-view-all">
                Ver todos os imóveis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          {loadingProps ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl bg-slate-100 aspect-[3/4] animate-pulse" />
              ))}
            </div>
          ) : shown.length === 0 ? (
            <p className="text-slate-500 text-center py-16" data-testid="featured-empty">
              Novos imóveis serão anunciados em breve.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {shown.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.12}>
                  <PropertyCard property={p} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#FAFAFA]" data-testid="institutional-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <div className="relative">
                <div className="clipped-frame overflow-hidden">
                  <img src="/empreendimentos-casavale.png" alt="Detalhe arquitetônico de empreendimento" className="w-full aspect-[4/5] object-cover" loading="lazy" />
                </div>
                <div className="absolute -bottom-5 -left-3 sm:-left-5 bg-brand text-white px-6 py-4 rounded-xl shadow-lg">
                  <p className="text-xs font-bold uppercase tracking-widest text-white/80">Casa Vale</p>
                  <p className="font-serif-display italic text-lg">Construindo espaços para viver bem.</p>
                </div>
              </div>
            </Reveal>
            <div>
              <Reveal>
                <p className="text-brand text-xs font-bold uppercase tracking-[0.3em] mb-3">A Construtora</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight" data-testid="institutional-heading">
                  Casa Vale — Construindo espaços para viver bem.
                </h2>
                <p className="mt-6 text-slate-600 leading-relaxed">
                  A Casa Vale Engenharia e Construção desenvolve imóveis que unem funcionalidade, conforto e atenção aos
                  detalhes. Nosso compromisso é oferecer espaços pensados para diferentes momentos da vida, valorizando
                  a qualidade construtiva e o bem-estar de quem escolhe viver em nossos empreendimentos.
                </p>
              </Reveal>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {PILARES.map((pilar, i) => (
                  <Reveal key={pilar.title} delay={0.1 + i * 0.08}>
                    <div className="flex gap-3.5">
                      <span className="shrink-0 w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center">
                        <pilar.icon className="w-5 h-5 text-brand" />
                      </span>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">{pilar.title}</h3>
                        <p className="mt-1 text-xs text-slate-500 leading-relaxed">{pilar.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.35}>
                <Link
                  to="/sobre"
                  className="mt-10 inline-flex items-center gap-2 text-sm font-bold text-slate-900 border-b-2 border-brand pb-1 hover:text-brand transition-colors duration-200"
                  data-testid="institutional-about-link"
                >
                  Conheça a Casa Vale <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 md:py-24 bg-brand overflow-hidden" data-testid="cta-section">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-limebrand/20 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight" data-testid="cta-heading">
              Encontre o imóvel ideal para você e sua família.
            </h2>
            <p className="mt-5 text-white/85 max-w-xl mx-auto leading-relaxed">
              Explore o catálogo completo e converse diretamente com nossa equipe pelo WhatsApp.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link
                to="/imoveis"
                className="inline-flex items-center gap-2 bg-white text-brand font-bold px-8 py-3.5 rounded-full hover:bg-slate-100 transition-colors duration-200"
                data-testid="cta-ver-imoveis"
              >
                Ver Imóveis Disponíveis
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
